// Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import log, { METRIC_NAME, TOOLBAR_METRIC_NAME } from "../../../../logging";
import { AuthState } from "../../../../types";
import { AuthStatus } from "../../../../enums";
import { AppStreamSDK } from "../../../../utils/AppStreamSDK";
import { FloatingToolbar } from "../../../toolbar/FloatingToolbar";
import { SessionErrorType } from "../../../../types/appStream";
import { AppStreamEmbedConstant } from "../../../../constants";
import { SESSION_STATE_STARTED_TIMEOUT } from "../../../../constants/Toolbar";

import { applyMode, Mode } from "@cloudscape-design/global-styles";
import "./embedStreamer.css";
import { useToolbarPreferenceStore } from "../../../../hooks/useToolbarPreferenceStore";
import {
  getHideToolbarFlag,
  getLegacyFlag,
} from "../../../../utils/toolbarItemUtils";

import { useSessionStatusListener } from "../../../../hooks/useSessionStatusListener";
import {
  convertErrorTypeFromEmbedEvent,
  getToolbarVersionFromUserInterface,
} from "../../../../utils/appstreamEventUtils";
import { ConditionalRenderErrorModal } from "../../../presession/ErrorModal";
import classNames from "classnames";
import Loader from "../../../Loader";
import { isDeviceOnExperimentalMode } from "../../../../utils/userAgentUtils";
import { useSessionDataStore } from "../../../../hooks/useSessionDataStore";
import useClipboardPermissions from "../../../../hooks/useClipboardPermissions";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const AppStream: any;
const APPSTREAM_DIV_ID = "appstream-container";

interface Props {
  endpoint: string;
  onSignOut: (authState: AuthState) => void;
  onRedirectToLogin: (redirectToLogin: boolean) => void;
}

const EmbeddedStreamer = (props: Props): JSX.Element => {
  // TODO For mobile users, present the legacy experience until we can resolve the on-screen keyboard issue. (https://issues.amazon.com/issues/LOWA-11655)
  const isLegacy = useMemo(
    () => getLegacyFlag() || isDeviceOnExperimentalMode(),
    []
  );
  const visualMode = useToolbarPreferenceStore((store) => store.visualMode);
  useEffect(() => {
    applyMode(visualMode);
  }, [visualMode]);

  const { endpoint, onSignOut, onRedirectToLogin } = props;
  const isUserDisconnected = useRef(false);
  const [isAppStreamIframeRendered, setIsAppStreamIframeRendered] = useState(
    false
  );
  const [sessionErrorType, setSessionErrorType] = useState<SessionErrorType>(
    "None"
  );

  const [appStreamSDK, setAppStreamSDK] = useState<AppStreamSDK>();

  const [spinnerStatus, setSpinnerStatus] = useState({
    spinnerVisible: true,
    message: "",
  });

  useEffect(() => {
    if (!isAppStreamIframeRendered || !endpoint) {
      return;
    }

    // TODO For mobile users, present the legacy experience until we can resolve the on-screen keyboard issue. (https://issues.amazon.com/issues/LOWA-11655)
    const isLegacy = getLegacyFlag() || isDeviceOnExperimentalMode();
    const hiddenElements = isLegacy
      ? undefined
      : [
          AppStreamEmbedConstant.ELEMENT_TOOLBAR_MAIN,
          AppStreamEmbedConstant.ELEMENT_ALERT,
        ];
    const appstreamOptions = {
      sessionURL: endpoint,
      userInterfaceConfig: {
        [AppStreamEmbedConstant.OPTION_HIDDEN_ELEMENTS]: hiddenElements,
      },
    };

    const sessionStartedNotReceivedCallback = () => {
      log.warn(
        "AS2 embed SESSION_STATE_CHANGE Started event not received after: " +
          SESSION_STATE_STARTED_TIMEOUT +
          " sec"
      );
    };
    const sessionStartedNotReceivedTimer = setTimeout(
      sessionStartedNotReceivedCallback,
      SESSION_STATE_STARTED_TIMEOUT * 1000
    );

    const embedInitialLoadStart = performance.now();
    const updateSessionStateCallback = (event) => {
      log.logMessage(
        "AS2 embed SESSION_STATE_CHANGE event: " + JSON.stringify(event)
      );
      if (
        event.sessionStatus ===
        AppStream.Embed.SessionStatus[AppStream.Embed.SessionStatus.Started]
      ) {
        // Publish how long it takes for AppStream to return its first frame status.
        log.publishTimeMetric(
          TOOLBAR_METRIC_NAME.SESSION_STARTED_RESPONSE_TIME,
          performance.now() - embedInitialLoadStart
        );
        clearTimeout(sessionStartedNotReceivedTimer);
      }
      // Sign out when the session is ended.
      // See event detail in https://tiny.amazon.com/nppg2ccj/docsawsamazappslatedeveembe.
      if (
        event.sessionStatus ===
        AppStream.Embed.SessionStatus[AppStream.Embed.SessionStatus.Ended]
      ) {
        log.logMessage("User ended the session. Signing out.");
        onSignOut({ authStatus: AuthStatus.SIGNING_OUT });
      }
      if (
        event.sessionStatus ===
        AppStream.Embed.SessionStatus[
          AppStream.Embed.SessionStatus.Disconnected
        ]
      ) {
        log.logMessage("User disconnected the session.");
        isUserDisconnected.current = true;
      }
    };

    const updateUserInterfaceStateCallback = (event) => {
      log.logMessage(
        "AS2 embed SESSION_INTERFACE_STATE_CHANGE event: " +
          JSON.stringify(event)
      );
      const toolbarVersion = getToolbarVersionFromUserInterface(event);
      if (toolbarVersion !== null) {
        log.publishMetricWithDimension(
          TOOLBAR_METRIC_NAME.TOOLBAR_VERSION,
          toolbarVersion
        );
      }
    };

    const errorCallback = (event) => {
      log.logMessage("AS2 embed SESSION_ERROR event: " + JSON.stringify(event));
      const errorType = convertErrorTypeFromEmbedEvent(event);
      setSessionErrorType(errorType);
      // There's an expected 500 error when user disconnects from session and session expires
      // See ticket: https://t.corp.amazon.com/P81729928
      if (event.errorCode === 500 && !isUserDisconnected.current) {
        log.publishCounterMetric(METRIC_NAME.APPSTREAM_EMBED_SERVER_ERROR);
      }
    };

    const updateSpinnerCallback = (event) => {
      log.logMessage(
        "AS2 embed SESSION_SPINNER event: " + JSON.stringify(event)
      );
      setSpinnerStatus(event);
    };

    const appStreamEmbed = new AppStream.Embed(
      APPSTREAM_DIV_ID,
      appstreamOptions
    );

    appStreamEmbed.addEventListener(
      AppStream.Embed.Events.SESSION_STATE_CHANGE,
      updateSessionStateCallback
    );
    appStreamEmbed.addEventListener(
      AppStream.Embed.Events.SESSION_INTERFACE_STATE_CHANGE,
      updateUserInterfaceStateCallback
    );
    appStreamEmbed.addEventListener(
      AppStream.Embed.Events.SESSION_ERROR,
      errorCallback
    );

    appStreamEmbed.addEventListener(
      AppStream.Embed.Events.SESSION_SPINNER,
      updateSpinnerCallback
    );

    if (!isLegacy) {
      setAppStreamSDK(new AppStreamSDK(appStreamEmbed));
    }
    return () => {
      clearTimeout(sessionStartedNotReceivedTimer);
    };
  }, [isAppStreamIframeRendered, endpoint, onSignOut]);

  useSessionStatusListener(appStreamSDK);
  const sessionState = useSessionDataStore((store) => store.sessionStatus);
  const showErrorModal =
    !isLegacy && sessionErrorType !== "None" && sessionState !== "Started";
  const hideToolbarFlag = useMemo(() => getHideToolbarFlag(), []);

  useClipboardPermissions();

  const showSpinner = !isLegacy && spinnerStatus.spinnerVisible;
  const showToolbar =
    !hideToolbarFlag && !isLegacy && sessionState === "Started" && !showSpinner;

  return (
    <div
      className={classNames(
        "embed-wrapper",
        visualMode === Mode.Light ? "light-visual-mode" : "dark-visual-mode"
      )}
    >
      {showSpinner && <Loader message={spinnerStatus.message} />}
      {showToolbar && <FloatingToolbar appStreamSDK={appStreamSDK} />}
      <div
        ref={(div) => {
          setIsAppStreamIframeRendered(!!div);
        }}
        className={classNames("appstream-container", {
          "appstream-container-hidden": showSpinner || showErrorModal,
        })}
        id={APPSTREAM_DIV_ID}
      />
      <ConditionalRenderErrorModal
        visible={showErrorModal}
        sessionErrorType={sessionErrorType}
        appStreamSDK={appStreamSDK}
        onRedirectToLogin={onRedirectToLogin}
      />
    </div>
  );
};

EmbeddedStreamer.propTypes = {
  endpoint: PropTypes.string.isRequired,
};

export default EmbeddedStreamer;
